/**
 * Media Hooks
 *
 * React Query mutation for the Easel media-upload endpoint, backing the
 * EaselImageField "Upload" tab. Mirrors the mutation shape used across the
 * other Easel hooks (useCollections / useJournal) — toast on error, typed result.
 */

import { useMutation } from '@tanstack/react-query'
import { mediaApi } from '../api/easelApi'
import { useToast } from '@/hooks/use-toast'
import type { EaselMediaKind, EaselMediaResult } from '../types'

/**
 * Upload an image file → GCS, returning `{ url, previewUrl, width, height, bytes }`.
 * Nothing is persisted server-side; the caller writes the returned URL onto its
 * own record (e.g. the journal post's imageUrl).
 */
export function useUploadEaselMedia() {
  const { toast } = useToast()

  return useMutation<EaselMediaResult, Error, { file: File; kind?: EaselMediaKind }>({
    mutationFn: ({ file, kind }) => mediaApi.upload(file, kind),
    onError: (error) => {
      toast({
        title: 'Upload failed',
        description: error.message || 'Could not upload the image. Please try again.',
        variant: 'destructive',
      })
    },
  })
}
